'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { apiClient } from '@/lib/api-client';
import {
  clearStoredTenantId,
  getStoredTenantId,
  isSystemTenantId,
  setStoredTenantId,
  subscribeAuthSession,
} from '@/lib/auth-storage';
import { useAppToast } from '@/hooks/useAppToast';
import { useTranslation } from 'react-i18next';
import { isAuthExemptPublicRoute } from '@/lib/i18n/public-serbian-routes';

export type SuperAdminTenantOption = {
  tenantId: string;
  label: string;
};

type SuperAdminTenantContextValue = {
  isSuperAdmin: boolean;
  isHydrated: boolean;
  selectedTenantId: string | null;
  selectedTenantLabel: string | null;
  tenantOptions: SuperAdminTenantOption[];
  isLoadingTenants: boolean;
  isAllTenants: boolean;
  isTenantScopeApplicable: boolean;
  setSelectedTenantId: (id: string | null) => void;
  /** Reload header tenant switcher options (call after create/edit/delete). */
  refreshTenantOptions: () => Promise<void>;
};

const SuperAdminTenantContext = createContext<SuperAdminTenantContextValue | null>(null);

const TENANT_SCOPE_EXCLUDED_PREFIXES = [
  '/tenants',
  '/admin/integrations',
  '/profile',
  '/logs',
] as const;

function readUserFromStorage(): { role?: string; tenantId?: string } | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem('user');
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as { role?: string; tenantId?: string };
  } catch {
    return null;
  }
}

function mapTenantOptions(items: unknown[]): SuperAdminTenantOption[] {
  return items
    .map((item) => {
      const row = item as Record<string, unknown>;
      const tenantId = String(row.tenantId ?? row.id ?? '').trim();
      if (!tenantId || isSystemTenantId(tenantId)) {
        return null;
      }
      const label = String(
        row.companyName ?? row.name ?? row.tenantName ?? tenantId
      ).trim();
      return { tenantId, label: label || tenantId };
    })
    .filter((row): row is SuperAdminTenantOption => row !== null)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export function isTenantScopeExcludedPath(pathname: string | null): boolean {
  if (!pathname) {
    return false;
  }
  return TENANT_SCOPE_EXCLUDED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function SuperAdminTenantProvider({ children }: { children: ReactNode }) {
  const { showWarning } = useAppToast();
  const { t } = useTranslation('common');
  const pathname = usePathname();

  const [isHydrated, setIsHydrated] = useState(false);
  const [user, setUser] = useState<{ role?: string; tenantId?: string } | null>(null);
  const [tenantOptions, setTenantOptions] = useState<SuperAdminTenantOption[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(null);
  const tenantsLoadedRef = useRef(false);

  const syncFromStorage = useCallback(() => {
    const storedUser = readUserFromStorage();
    const superAdmin = storedUser?.role === 'SuperAdmin';
    setUser(storedUser);
    if (superAdmin) {
      setSelectedTenantIdState(getStoredTenantId());
      return;
    }

    const fixed = storedUser?.tenantId?.trim() || null;
    setSelectedTenantIdState(fixed && !isSystemTenantId(fixed) ? fixed : null);
    setTenantOptions([]);
    tenantsLoadedRef.current = false;
  }, []);

  useLayoutEffect(() => {
    syncFromStorage();
    setIsHydrated(true);
    return subscribeAuthSession(syncFromStorage);
  }, [syncFromStorage]);

  const isSuperAdmin = isHydrated && user?.role === 'SuperAdmin';
  const fixedTenantId = !isSuperAdmin ? user?.tenantId?.trim() || null : null;

  const isTenantScopeApplicable = !isTenantScopeExcludedPath(pathname);
  const isAllTenants = isSuperAdmin ? selectedTenantId === null : false;

  const selectedTenantLabel = useMemo(() => {
    if (!isSuperAdmin || !selectedTenantId) {
      return null;
    }
    return tenantOptions.find((o) => o.tenantId === selectedTenantId)?.label ?? selectedTenantId;
  }, [isSuperAdmin, selectedTenantId, tenantOptions]);

  const loadTenantOptions = useCallback(async () => {
    if (!isSuperAdmin) {
      return;
    }
    setIsLoadingTenants(true);
    try {
      const result = await apiClient.getAdminTenants({ page: 1, pageSize: 2000 });
      const options = mapTenantOptions(result.data ?? []);
      setTenantOptions(options);
      tenantsLoadedRef.current = true;

      const stored = getStoredTenantId();
      if (stored && !options.some((o) => o.tenantId === stored)) {
        clearStoredTenantId();
        setSelectedTenantIdState(null);
        showWarning(t('superAdmin.tenantSelector.invalidTenantCleared'));
      }
    } catch {
      setTenantOptions([]);
    } finally {
      setIsLoadingTenants(false);
    }
  }, [isSuperAdmin, showWarning, t]);

  const refreshTenantOptions = useCallback(async () => {
    if (!isSuperAdmin) {
      return;
    }
    tenantsLoadedRef.current = false;
    await loadTenantOptions();
  }, [isSuperAdmin, loadTenantOptions]);

  useEffect(() => {
    if (!isSuperAdmin || tenantsLoadedRef.current) {
      return;
    }
    if (isAuthExemptPublicRoute(pathname)) {
      return;
    }
    void loadTenantOptions();
  }, [isSuperAdmin, pathname, loadTenantOptions]);

  const setSelectedTenantId = useCallback(
    (id: string | null) => {
      if (!isSuperAdmin) {
        return;
      }

      if (!id) {
        clearStoredTenantId();
        setSelectedTenantIdState(null);
        return;
      }

      const trimmed = id.trim();
      if (!trimmed || isSystemTenantId(trimmed)) {
        clearStoredTenantId();
        setSelectedTenantIdState(null);
        return;
      }

      setStoredTenantId(trimmed);
      setSelectedTenantIdState(trimmed);
    },
    [isSuperAdmin]
  );

  const value = useMemo<SuperAdminTenantContextValue>(
    () => ({
      isSuperAdmin,
      isHydrated,
      selectedTenantId: isSuperAdmin ? selectedTenantId : fixedTenantId,
      selectedTenantLabel: isSuperAdmin ? selectedTenantLabel : null,
      tenantOptions,
      isLoadingTenants,
      isAllTenants,
      isTenantScopeApplicable,
      setSelectedTenantId,
      refreshTenantOptions,
    }),
    [
      fixedTenantId,
      isAllTenants,
      isHydrated,
      isLoadingTenants,
      isSuperAdmin,
      isTenantScopeApplicable,
      refreshTenantOptions,
      selectedTenantId,
      selectedTenantLabel,
      setSelectedTenantId,
      tenantOptions,
    ]
  );

  return (
    <SuperAdminTenantContext.Provider value={value}>
      {children}
    </SuperAdminTenantContext.Provider>
  );
}

export function useSuperAdminTenantContext(): SuperAdminTenantContextValue {
  const ctx = useContext(SuperAdminTenantContext);
  if (!ctx) {
    throw new Error('useSuperAdminTenantContext must be used within SuperAdminTenantProvider');
  }
  return ctx;
}

export function useEffectiveTenantId(): string | undefined {
  const {
    isSuperAdmin,
    isAllTenants,
    selectedTenantId,
    isTenantScopeApplicable,
  } = useSuperAdminTenantContext();

  if (!isSuperAdmin) {
    return selectedTenantId ?? undefined;
  }

  if (!isTenantScopeApplicable || isAllTenants) {
    return undefined;
  }

  return selectedTenantId ?? undefined;
}
